import { useCallback, useState } from "react";
import { add, setMilliseconds, setMinutes, setSeconds } from "date-fns";

import { Button, ContentThumbnail, Drawer } from "@/components";
import { Video } from "@/api";
import {
  getContentThumbnailUrl,
  getContentTitle,
  getDateStringFromDateTime,
} from "@/utils";
import { Schedule } from "@/icons";

import { ReadOnlyInput, Select } from "@/components";
import { MiniCalendar } from "./MiniCalendar.tsx";
import { usePutEvent } from "@/hooks";
import {
  EVENT_TYPE_SCHEDULED,
  EVENT_TYPE_WATCHED,
  ITEM_TYPE_VIDEO,
  QUERY_PARENT_ID,
} from "@/const.ts";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { CompleteToast } from "../../ContentDetails/components/CompleteToast.tsx";
import { COMPLETE_TOAST_CONFIG } from "../../ContentDetails/components/const.ts";
import { useTranslate } from "@/providers/context";

interface CalendarDrawerProps {
  title?: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  video: Video;
  isCompletePreviously?: boolean;
}

const getDefaultDate = () => {
  const nextHour = add(new Date(), { hours: 1 });
  return setMilliseconds(setSeconds(setMinutes(nextHour, 0), 0), 0);
};
const generateArray = (start: number, end: number, increment = 1) => {
  const arr = [];
  for (let i = start; i <= end; i += increment) {
    arr.push({
      name: i.toString().padStart(2, "0"), // Zero-pad the number
      value: i.toString(),
    });
  }
  return arr;
};
const HOUR_OPTIONS = generateArray(1, 12);
const MINUTE_OPTIONS = generateArray(0, 55, 5);

export const CalendarDrawer = ({
  title,
  open,
  setOpen,
  video,
  isCompletePreviously,
}: CalendarDrawerProps) => {
  const [scheduleDate, setScheduleDate] = useState<Date>(getDefaultDate());
  const [showCalendar, setShowCalendar] = useState(false);
  const { mutate: putEventMutate } = usePutEvent();
  const [query] = useSearchParams();
  const parentId = query.get(QUERY_PARENT_ID);
  const { translate, formatDate, formatDetailedDatetime } = useTranslate();

  // Reset state without unmounting
  const reset = useCallback(() => {
    setScheduleDate(getDefaultDate());
    setShowCalendar(false);
  }, []);

  const onSave = useCallback(() => {
    putEventMutate(
      {
        ItemId: video.VideoId,
        ItemType: ITEM_TYPE_VIDEO,
        SourceId: video.SourceId,
        DataSource: video.DataSource,
        EventDate: getDateStringFromDateTime(scheduleDate),
        EventType: isCompletePreviously
          ? EVENT_TYPE_WATCHED
          : EVENT_TYPE_SCHEDULED,
        ItemParentId:
          isCompletePreviously && parentId ? parseInt(parentId) : undefined,
      },
      {
        onSuccess: () => {
          isCompletePreviously
            ? toast(
                <CompleteToast
                  message={`${translate("Video marked complete on")} ${formatDetailedDatetime(scheduleDate)}`}
                />,
                COMPLETE_TOAST_CONFIG,
              )
            : toast(
                `${translate("Successfully scheduled for")} ${formatDetailedDatetime(scheduleDate)}`,
              );

          reset();
        },
      },
    );

    setOpen(false);
  }, [
    isCompletePreviously,
    putEventMutate,
    reset,
    scheduleDate,
    setOpen,
    video.DataSource,
    video.SourceId,
    video.VideoId,
    parentId,
    translate,
    formatDetailedDatetime,
  ]);

  return (
    <>
      <Drawer
        title={title ?? translate("Schedule")}
        open={open}
        onOpenChange={setOpen}
        dataTestId="schedule-drawer"
      >
        <div className="flex h-full flex-col">
          <ContentThumbnail
            thumbnail={getContentThumbnailUrl(video)}
            contentItem={video}
            className="mb-3 flex-shrink-0 rounded-xl"
          />
          <p className="p2-medium">{getContentTitle(video)}</p>
          <div className="my-7 w-full border-b border-border" />
          <div className="flex">
            <ReadOnlyInput
              data-testid="schedule-drawer-date"
              className="mr-2 w-1/2 flex-grow cursor-pointer hover:bg-surface-secondary"
              onClick={() => setShowCalendar(true)}
            >
              <Schedule className="mr-1 hidden xl:block" />
              {formatDate(scheduleDate, true)}
            </ReadOnlyInput>
            <Select
              className="mr-0.5"
              width={45}
              options={HOUR_OPTIONS}
              value={(scheduleDate.getHours() % 12 || 12).toString()}
              onValueChange={(value) =>
                setScheduleDate((prevDate) => {
                  const newDate = new Date(prevDate);
                  const hours = parseInt(value);
                  const currentHours = prevDate.getHours();
                  newDate.setHours(hours + (currentHours > 12 ? 12 : 0));
                  return newDate;
                })
              }
            />
            <div className="p2-medium mr-0.5 flex items-center">:</div>
            <Select
              width={45}
              className="mr-2"
              options={MINUTE_OPTIONS}
              value={scheduleDate.getMinutes().toString()}
              onValueChange={(value) => {
                setScheduleDate((prevDate) => {
                  const newDate = new Date(prevDate); // create a new date to avoid mutating the existing one
                  const minutes = parseInt(value); // convert value to integer
                  newDate.setMinutes(minutes); // always set PM by adding 12
                  return newDate;
                });
              }}
            />
            <Select
              width={45}
              options={[
                { name: "AM", value: "AM" },
                { name: "PM", value: "PM" },
              ]}
              value={scheduleDate.getHours() >= 12 ? "PM" : "AM"}
              onValueChange={(value) => {
                setScheduleDate((prevDate) => {
                  const newDate = new Date(prevDate); // create a new date to avoid mutating the existing one
                  const currentHours = newDate.getHours();
                  if (value === "PM" && currentHours < 12) {
                    newDate.setHours(currentHours + 12); // convert to PM by adding 12 to the hours
                  } else if (value === "AM" && currentHours >= 12) {
                    newDate.setHours(currentHours - 12); // convert to AM by subtracting 12 from the hours
                  }
                  return newDate;
                });
              }}
            />
          </div>
          {showCalendar && (
            <div className="pt-9">
              <MiniCalendar
                selectedDate={scheduleDate}
                setSelectedDate={setScheduleDate}
                isCompletePreviously={isCompletePreviously}
              />
            </div>
          )}
          <div className="flex flex-shrink-0 flex-grow items-end pb-8">
            <Button
              size="m"
              className="w-full"
              onClick={onSave}
              data-testid="schedule-save-button"
              disabled={!showCalendar}
            >
              {isCompletePreviously
                ? translate("Mark Complete")
                : translate("Confirm")}
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
};
